import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── icons ───────────────────────────────────────────── */

const Icon = ({ children, size = 18, strokeWidth = 1.4 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    {children}
  </svg>
);

const PlusIcon = (p) => <Icon {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>;
const CheckIcon = (p) => <Icon {...p} strokeWidth={2}><path d="m4 12.5 5 5L20 6.5" /></Icon>;
const ChevronIcon = ({ open, ...p }) => (
  <Icon {...p}>
    <path d={open ? 'm6 15 6-6 6 6' : 'm9 6 6 6-6 6'} />
  </Icon>
);
const TrashIcon = (p) => (
  <Icon {...p}>
    <path d="M4 7h16" /><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M6.5 7 7.5 19a1.5 1.5 0 0 0 1.5 1.4h6a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
  </Icon>
);
const CalendarIcon = (p) => (
  <Icon {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17" /><path d="M8 3v4" /><path d="M16 3v4" />
  </Icon>
);
const RepeatIcon = (p) => (
  <Icon {...p}>
    <path d="M4 9.5A4 4 0 0 1 8 5.5h10" /><path d="m15 2.5 3 3-3 3" />
    <path d="M20 14.5a4 4 0 0 1-4 4H6" /><path d="m9 21.5-3-3 3-3" />
  </Icon>
);
const BackIcon = (p) => <Icon {...p}><path d="M19 12H6" /><path d="m11 6-6 6 6 6" /></Icon>;
const CloseIcon = (p) => <Icon {...p}><path d="M6 6l12 12" /><path d="M18 6 6 18" /></Icon>;

/* ── helpers ─────────────────────────────────────────── */

const PRIORITY_COLORS = {
  none: 'transparent',
  low: '#5a7a6a',
  medium: '#c9935a',
  high: '#b5634f',
};

const WEEKDAYS = [
  { code: 'MO', label: 'm' }, { code: 'TU', label: 't' }, { code: 'WE', label: 'w' },
  { code: 'TH', label: 't' }, { code: 'FR', label: 'f' }, { code: 'SA', label: 's' },
  { code: 'SU', label: 's' },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDue = (dateStr) => {
  if (!dateStr) return null;
  const today = todayStr();
  if (dateStr === today) return { text: 'today', overdue: false };
  if (dateStr < today) return { text: shortDate(dateStr), overdue: true };
  const t = new Date(today + 'T00:00:00');
  t.setDate(t.getDate() + 1);
  const tomorrow = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  if (dateStr === tomorrow) return { text: 'tomorrow', overdue: false };
  return { text: shortDate(dateStr), overdue: false };
};

const shortDate = (dateStr) => {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const [y, m, d] = dateStr.split('-');
  const thisYear = String(new Date().getFullYear());
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}${y === thisYear ? '' : ` ${y}`}`;
};

const recurLabel = (task) => {
  if (!task.recur_frequency) return null;
  const n = task.recur_interval || 1;
  if (task.recur_frequency === 'weekly' && task.recur_days) {
    return task.recur_days.toLowerCase().split(',').join(' ');
  }
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[task.recur_frequency];
  return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`;
};

const CATEGORY_COLORS = ['#c9935a', '#8a9a7b', '#a1748f', '#7b8aa1', '#b5634f', '#8a7fa1'];

export default function Todos() {
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLORS[0]);
  const [newTask, setNewTask] = useState({});
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const savingRef = useRef(false);

  const loadAll = async (withCompleted = showCompleted) => {
    try {
      const [catRes, taskRes] = await Promise.all([
        axios.get(`${API}/todos/categories?token=${token}`),
        axios.get(`${API}/todos/tasks?token=${token}&include_completed=${withCompleted}`),
      ]);
      setCategories(catRes.data.categories || []);
      setTasks(taskRes.data.tasks || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const toggleCompleted = () => {
    const next = !showCompleted;
    setShowCompleted(next);
    loadAll(next);
  };

  /* ── actions ── */

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/todos/categories`, {
        token, name, color: newCategoryColor,
      });
      setNewCategoryName('');
      setNewCategoryColor(CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length]);
      setAddingCategory(false);
      loadAll();
    } catch (err) { console.log(err); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('delete this category? the tasks inside will move to uncategorized.')) return;
    try {
      await axios.delete(`${API}/todos/categories/${id}?token=${token}`);
      loadAll();
    } catch (err) { console.log(err); }
  };

  const addTask = async (categoryId) => {
    const key = categoryId || 'none';
    const title = (newTask[key] || '').trim();
    if (!title) return;
    try {
      await axios.post(`${API}/todos/tasks`, {
        token, title, category_id: categoryId, priority: 'none',
      });
      setNewTask(prev => ({ ...prev, [key]: '' }));
      loadAll();
    } catch (err) { console.log(err); }
  };

  const completeTask = async (task) => {
    const nextState = !task.is_completed;
    // optimistic — the row settles when the refetch lands
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, is_completed: nextState } : t)));
    try {
      await axios.post(`${API}/todos/tasks/${task.id}/complete`, {
        token, is_completed: nextState,
      });
      loadAll();
    } catch (err) {
      console.log(err);
      loadAll();
    }
  };

  const patchTask = async (taskId, fields) => {
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...fields } : t)));
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      await axios.put(`${API}/todos/tasks/${taskId}`, { token, ...fields });
    } catch (err) { console.log(err); }
    finally { savingRef.current = false; }
  };

  const deleteTask = async (task) => {
    const series = task.series_id && task.recur_frequency;
    let deleteSeries = false;
    if (series) {
      deleteSeries = window.confirm(
        'this task repeats. OK deletes every occurrence including past ones, cancel deletes just this one.'
      );
    }
    try {
      await axios.delete(
        `${API}/todos/tasks/${task.id}?token=${token}&delete_series=${deleteSeries}`
      );
      setExpandedId(null);
      loadAll();
    } catch (err) { console.log(err); }
  };

  const toggleWeekday = (task, code) => {
    const current = (task.recur_days || '').split(',').filter(Boolean);
    const next = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    const ordered = WEEKDAYS.map(w => w.code).filter(c => next.includes(c));
    patchTask(task.id, { recur_days: ordered.join(',') });
  };

  /* ── styles ── */

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
      flexShrink: 0, zIndex: 50,
    },
    backBtn: {
      background: 'transparent', border: 'none', color: '#4a3d30',
      cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0,
    },
    headerTitle: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '22px',
      fontWeight: 300, fontStyle: 'italic', color: '#e8ddd0',
    },
    iconBtn: {
      background: 'transparent', border: 'none', color: '#c9935a',
      cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px',
    },
    body: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '24px 24px 100px', maxWidth: '680px', width: '100%',
      margin: '0 auto', boxSizing: 'border-box',
    },
    topRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: '20px', gap: '12px',
    },
    countText: { fontSize: '13px', color: '#4a3d30' },
    toggleBtn: (active) => ({
      border: 'none', padding: '5px 12px', borderRadius: '14px',
      fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: active ? '#c9935a' : '#4a3d30',
      background: active ? 'rgba(201,147,90,0.08)' : 'transparent',
      cursor: 'pointer', flexShrink: 0,
    }),
    section: { marginBottom: '26px' },
    sectionHead: {
      display: 'flex', alignItems: 'center', gap: '8px',
      marginBottom: '10px', cursor: 'pointer',
    },
    catDot: (color) => ({
      width: '8px', height: '8px', borderRadius: '50%',
      background: color, flexShrink: 0,
    }),
    catName: {
      fontSize: '11px', letterSpacing: '0.16em', color: '#8a7a6a',
      textTransform: 'uppercase', flex: 1,
    },
    catCount: { fontSize: '11px', color: '#4a3d30' },
    chevronWrap: { color: '#4a3d30', display: 'flex', alignItems: 'center' },
    taskRow: {
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      padding: '13px 14px', background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.06)', borderRadius: '14px',
      marginBottom: '7px', position: 'relative', overflow: 'hidden',
    },
    priorityBar: (color) => ({
      position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: color,
    }),
    checkbox: (done) => ({
      width: '21px', height: '21px', borderRadius: '50%', flexShrink: 0,
      border: done ? '1px solid #c9935a' : '1px solid rgba(201,147,90,0.28)',
      background: done ? 'rgba(201,147,90,0.15)' : 'transparent',
      color: done ? '#c9935a' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', padding: 0, marginTop: '1px',
    }),
    taskMain: { flex: 1, minWidth: 0, cursor: 'pointer' },
    taskTitle: (done) => ({
      fontSize: '14.5px', color: done ? '#4a3d30' : '#e8ddd0',
      textDecoration: done ? 'line-through' : 'none', lineHeight: 1.45,
      wordBreak: 'break-word',
    }),
    metaRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap' },
    metaChip: (overdue) => ({
      display: 'flex', alignItems: 'center', gap: '5px',
      fontSize: '11px', color: overdue ? '#b5634f' : '#4a3d30',
    }),
    detail: {
      padding: '4px 14px 16px 47px', background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.06)', borderTop: 'none',
      borderRadius: '0 0 14px 14px', marginTop: '-8px', marginBottom: '7px',
    },
    detailLabel: {
      fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase',
      color: '#4a3d30', marginBottom: '7px', marginTop: '14px',
    },
    input: {
      width: '100%', boxSizing: 'border-box', background: '#0e0b0f',
      border: '1px solid rgba(201,147,90,0.12)', borderRadius: '10px',
      padding: '9px 11px', color: '#e8ddd0', fontSize: '13px',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, outline: 'none',
    },
    textarea: {
      width: '100%', boxSizing: 'border-box', background: '#0e0b0f',
      border: '1px solid rgba(201,147,90,0.12)', borderRadius: '10px',
      padding: '9px 11px', color: '#e8ddd0', fontSize: '13px',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      minHeight: '70px', resize: 'vertical', outline: 'none', lineHeight: 1.6,
    },
    pillRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    pill: (active, color) => ({
      padding: '6px 12px', borderRadius: '16px', fontSize: '11.5px',
      border: `1px solid ${active ? (color || '#c9935a') : 'rgba(201,147,90,0.12)'}`,
      background: active ? `${color || '#c9935a'}1f` : 'transparent',
      color: active ? (color || '#c9935a') : '#4a3d30',
      cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    }),
    dayBtn: (active) => ({
      width: '30px', height: '30px', borderRadius: '50%', fontSize: '11px',
      border: `1px solid ${active ? '#c9935a' : 'rgba(201,147,90,0.12)'}`,
      background: active ? 'rgba(201,147,90,0.12)' : 'transparent',
      color: active ? '#c9935a' : '#4a3d30', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, padding: 0,
    }),
    deleteRow: {
      display: 'flex', alignItems: 'center', gap: '7px', marginTop: '18px',
      color: '#b5634f', fontSize: '12px', cursor: 'pointer',
      background: 'transparent', border: 'none', padding: 0,
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    addTaskRow: {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '11px 14px', borderRadius: '14px',
      border: '1px dashed rgba(201,147,90,0.12)', marginTop: '2px',
    },
    addTaskInput: {
      flex: 1, background: 'transparent', border: 'none', outline: 'none',
      color: '#e8ddd0', fontSize: '13.5px',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    newCatCard: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '16px', padding: '16px', marginBottom: '24px',
    },
    rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
    colorDot: (color, active) => ({
      width: '24px', height: '24px', borderRadius: '50%', background: color,
      border: active ? '2px solid #e8ddd0' : '2px solid transparent',
      cursor: 'pointer', padding: 0,
    }),
    primaryBtn: {
      padding: '9px 18px', borderRadius: '18px', border: '1px solid rgba(201,147,90,0.25)',
      background: 'rgba(201,147,90,0.1)', color: '#c9935a', fontSize: '12.5px',
      cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    empty: {
      background: '#1e1825', border: '1px dashed rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '40px 24px', textAlign: 'center',
      fontSize: '14px', color: '#4a3d30', lineHeight: 1.8,
    },
  };

  /* ── render pieces ── */

  const renderDetail = (task) => (
    <div style={s.detail}>
      <div style={s.detailLabel}>title</div>
      <input
        style={s.input}
        defaultValue={task.title}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== task.title) patchTask(task.id, { title: v });
        }}
      />

      <div style={s.detailLabel}>notes</div>
      <textarea
        style={s.textarea}
        defaultValue={task.notes}
        placeholder="anything worth remembering about this one"
        onBlur={(e) => {
          if (e.target.value !== task.notes) patchTask(task.id, { notes: e.target.value });
        }}
      />

      <div style={s.detailLabel}>due date</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="date"
          style={{ ...s.input, flex: 1, colorScheme: 'dark' }}
          value={task.due_date || ''}
          onChange={(e) => patchTask(task.id, { due_date: e.target.value })}
        />
        {task.due_date && (
          <button
            style={s.iconBtn}
            onClick={() => patchTask(task.id, { due_date: '' })}
            aria-label="clear due date"
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      <div style={s.detailLabel}>priority</div>
      <div style={s.pillRow}>
        {['none', 'low', 'medium', 'high'].map(p => (
          <button
            key={p}
            style={s.pill(task.priority === p, PRIORITY_COLORS[p] === 'transparent' ? null : PRIORITY_COLORS[p])}
            onClick={() => patchTask(task.id, { priority: p })}
          >
            {p}
          </button>
        ))}
      </div>

      <div style={s.detailLabel}>repeats</div>
      <div style={s.pillRow}>
        {[['', 'never'], ['daily', 'daily'], ['weekly', 'weekly'], ['monthly', 'monthly'], ['yearly', 'yearly']].map(([val, label]) => (
          <button
            key={label}
            style={s.pill((task.recur_frequency || '') === val)}
            onClick={() => patchTask(task.id, { recur_frequency: val })}
          >
            {label}
          </button>
        ))}
      </div>

      {task.recur_frequency === 'weekly' && (
        <>
          <div style={s.detailLabel}>on these days</div>
          <div style={s.pillRow}>
            {WEEKDAYS.map((w, i) => (
              <button
                key={i}
                style={s.dayBtn((task.recur_days || '').split(',').includes(w.code))}
                onClick={() => toggleWeekday(task, w.code)}
              >
                {w.label}
              </button>
            ))}
          </div>
        </>
      )}

      {task.recur_frequency && task.recur_frequency !== 'weekly' && (
        <>
          <div style={s.detailLabel}>every</div>
          <input
            type="number" min="1"
            style={{ ...s.input, width: '90px' }}
            value={task.recur_interval || 1}
            onChange={(e) => patchTask(task.id, { recur_interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
          />
        </>
      )}

      {categories.length > 0 && (
        <>
          <div style={s.detailLabel}>category</div>
          <div style={s.pillRow}>
            {categories.map(c => (
              <button
                key={c.id}
                style={s.pill(task.category_id === c.id, c.color)}
                onClick={() => patchTask(task.id, { category_id: task.category_id === c.id ? '' : c.id })}
              >
                {c.name}
              </button>
            ))}
          </div>
        </>
      )}

      <button style={s.deleteRow} onClick={() => deleteTask(task)}>
        <TrashIcon size={15} /> delete task
      </button>
    </div>
  );

  const renderTask = (task) => {
    const due = formatDue(task.due_date);
    const repeat = recurLabel(task);
    const isOpen = expandedId === task.id;
    return (
      <div key={task.id}>
        <div style={{
          ...s.taskRow,
          ...(isOpen ? { borderRadius: '14px 14px 0 0', marginBottom: 0, borderBottom: 'none' } : {}),
        }}>
          {task.priority !== 'none' && <div style={s.priorityBar(PRIORITY_COLORS[task.priority])} />}
          <button
            style={s.checkbox(task.is_completed)}
            onClick={() => completeTask(task)}
            aria-label={task.is_completed ? 'mark as not done' : 'mark as done'}
          >
            <CheckIcon size={12} />
          </button>
          <div style={s.taskMain} onClick={() => setExpandedId(isOpen ? null : task.id)}>
            <div style={s.taskTitle(task.is_completed)}>{task.title}</div>
            {(due || repeat || task.notes) && (
              <div style={s.metaRow}>
                {due && (
                  <span style={s.metaChip(due.overdue)}>
                    <CalendarIcon size={13} /> {due.text}
                  </span>
                )}
                {repeat && (
                  <span style={s.metaChip(false)}>
                    <RepeatIcon size={13} /> {repeat}
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            style={s.chevronWrap}
            onClick={() => setExpandedId(isOpen ? null : task.id)}
          >
            <ChevronIcon open={isOpen} size={16} />
          </div>
        </div>
        {isOpen && renderDetail(task)}
      </div>
    );
  };

  const renderSection = (category) => {
    const catId = category ? category.id : null;
    const key = catId || 'none';
    const sectionTasks = tasks.filter(t =>
      category ? t.category_id === catId : !t.category_id
    );
    if (!category && sectionTasks.length === 0) return null;
    const isCollapsed = collapsed[key];
    const openCount = sectionTasks.filter(t => !t.is_completed).length;

    return (
      <div key={key} style={s.section}>
        <div
          style={s.sectionHead}
          onClick={() => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
        >
          <div style={s.catDot(category ? category.color : '#4a3d30')} />
          <div style={s.catName}>{category ? category.name : 'uncategorized'}</div>
          <div style={s.catCount}>{openCount}</div>
          <div style={s.chevronWrap}><ChevronIcon open={!isCollapsed} size={15} /></div>
          {category && (
            <button
              style={{ ...s.iconBtn, color: '#4a3d30' }}
              onClick={(e) => { e.stopPropagation(); deleteCategory(category.id); }}
              aria-label={`delete ${category.name}`}
            >
              <TrashIcon size={14} />
            </button>
          )}
        </div>

        {!isCollapsed && (
          <>
            {sectionTasks.map(renderTask)}
            <div style={s.addTaskRow}>
              <span style={{ color: '#4a3d30', display: 'flex' }}><PlusIcon size={15} /></span>
              <input
                style={s.addTaskInput}
                placeholder="add a task"
                value={newTask[key] || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, [key]: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && addTask(catId)}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const openTotal = tasks.filter(t => !t.is_completed).length;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />
      <style>{`
        input::placeholder, textarea::placeholder { color: #4a3d30; }
        input:focus, textarea:focus { border-color: rgba(201,147,90,0.3) !important; }
        button:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
      `}</style>

      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/companions')} aria-label="back">
          <BackIcon size={20} />
        </button>
        <div style={s.headerTitle}>to-do</div>
        <button
          style={s.iconBtn}
          onClick={() => setAddingCategory(v => !v)}
          aria-label="new category"
        >
          {addingCategory ? <CloseIcon size={20} /> : <PlusIcon size={20} />}
        </button>
      </header>

      <div style={s.body}>
        <div style={s.topRow}>
          <div style={s.countText}>
            {loading ? 'loading…' : openTotal === 0 ? 'nothing left ♡' : `${openTotal} open`}
          </div>
          <button style={s.toggleBtn(showCompleted)} onClick={toggleCompleted}>
            {showCompleted ? 'hide done' : 'show done'}
          </button>
        </div>

        {addingCategory && (
          <div style={s.newCatCard}>
            <input
              style={s.input}
              placeholder="category name"
              value={newCategoryName}
              autoFocus
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
            />
            <div style={{ ...s.rowBetween, marginTop: '14px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {CATEGORY_COLORS.map(c => (
                  <button
                    key={c}
                    style={s.colorDot(c, newCategoryColor === c)}
                    onClick={() => setNewCategoryColor(c)}
                    aria-label={`color ${c}`}
                  />
                ))}
              </div>
              <button style={s.primaryBtn} onClick={addCategory}>add</button>
            </div>
          </div>
        )}

        {!loading && categories.length === 0 && tasks.length === 0 ? (
          <div style={s.empty}>
            nothing here yet.<br />
            start with a category, then fill it in.
          </div>
        ) : (
          <>
            {categories.map(renderSection)}
            {renderSection(null)}
          </>
        )}
      </div>
    </div>
  );
}
